/**
 * Google Analytics 4 (GA4) helper for React SPA.
 *
 * - Sends virtual pageviews on every react-router navigation
 * - Provides typed event helpers for CTA clicks, conversions, scroll depth
 * - Safe no-op when gtag is missing (dev / ad-blockers)
 */

const GA_ID = 'G-T47XH244MY';

function gtag(...args) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

// ── Pageview (called on every route change) ────────────────────────────────
export function trackPageView(path, title) {
  gtag('config', GA_ID, {
    page_path: path,
    page_title: title || document.title,
  });
}

// ── Generic custom event ────────────────────────────────────────────────────
export function trackEvent(eventName, params = {}) {
  gtag('event', eventName, params);
}

// ── CTA / button clicks ────────────────────────────────────────────────────
export function trackCTAClick(ctaName, pagePath) {
  trackEvent('cta_click', {
    cta_name: ctaName,
    page_path: pagePath || window.location.pathname,
  });
}

// ── Sign-up funnel ──────────────────────────────────────────────────────────
export function trackSignUpStart(method = 'email') {
  trackEvent('sign_up_start', { method });
}

export function trackSignUpComplete(method = 'email') {
  trackEvent('sign_up', { method }); // GA4 recommended event name
}

// ── Registration form funnel ────────────────────────────────────────────────
export function trackRegFieldFocus(fieldName) {
  trackEvent('reg_field_focus', { field_name: fieldName });
}

export function trackRegSubmitAttempt() {
  trackEvent('reg_submit_attempt');
}

export function trackRegValidationError(errorType) {
  trackEvent('reg_validation_error', { error_type: errorType });
}

export function trackRegServerError(errorType) {
  trackEvent('reg_server_error', { error_type: errorType });
}

export function trackRegEmailVerificationShown() {
  trackEvent('reg_email_verification_shown');
}

// ── App download clicks ─────────────────────────────────────────────────────
export function trackDownloadClick(platform) {
  trackEvent('app_download_click', { platform }); // 'ios' | 'android'
}

// ── Preview / Try mode ──────────────────────────────────────────────────────
export function trackPreviewStart() {
  trackEvent('preview_start');
}

// ── Scroll depth (25 / 50 / 75 / 100%) ─────────────────────────────────────
export function trackScrollDepth(percent, pagePath) {
  trackEvent('scroll_depth', {
    percent,
    page_path: pagePath || window.location.pathname,
  });
}

// ── Thought selection on conversion pages ───────────────────────────────────
export function trackThoughtSelect(thought, pagePath) {
  trackEvent('thought_select', {
    thought,
    page_path: pagePath || window.location.pathname,
  });
}

// ── Outbound link clicks ────────────────────────────────────────────────────
export function trackOutboundClick(url) {
  trackEvent('outbound_click', {
    url,
    transport_type: 'beacon',
  });
}
